import React from 'react'
import { Helmet } from 'react-helmet'

import PeractoEditor from '@peracto/peracto-editor'
import { useConfig } from '@peracto/peracto-config'

// These components contain a lot of JS in their form/edit modes
// which isn't needed on the storefront, so in the editor they're imported from this
// package instead of the 'light' version in @peracto/peracto-blocks-tailwind
import {
    textPlugin,
    accordionPlugin,
    carouselPlugin,
    imagePlugin,
    videoPlugin,
    youtubePlugin,
    embedPlugin,
    mapPlugin,
    formPlugin,
    buttonsPlugin,
    menuItemsPlugin,
    menuItemsCarouselPlugin,
    productsPlugin,
    productsCarouselPlugin,
    containerPlugin,
    panelsPlugin,
} from './ContentBlocks'

const ContentIndex = () => {
    const peractoConfig = useConfig()
    const { blocks = {}, stylesheets = [], theme } = peractoConfig.get('editorConfig') || {}

    const { searchApiKey } = peractoConfig.get('algolia') || {}

    // These are Peracto's own blocks: always available in the editor with their full
    // form/edit mode, regardless of what a site's blocks/index.js declares. A site can
    // still swap in a genuine custom implementation by providing a block of the same
    // reserved name (see the customXBlock checks below), which replaces the entry here.
    const defaultBlocks = [
        textPlugin(),
        videoPlugin(),
        youtubePlugin(),
        mapPlugin(),
        formPlugin(),
        accordionPlugin(),
        carouselPlugin(),
        menuItemsCarouselPlugin(),
    ]

    let Container = containerPlugin({ defaultPlugin: textPlugin() })

    // Container override if custom container is provided
    const customContainerPlugin = blocks?.plugins?.layout?.find(
        i => i.name === 'peracto/layout/container'
    )

    if (customContainerPlugin) {
        Container = customContainerPlugin
    }

    const filteredPlugins = []

    const customPanelsBlock = blocks.plugins?.content?.find(i => i.name.endsWith('content/panels'))

    if (customPanelsBlock) {
        filteredPlugins.unshift(customPanelsBlock)
    } else {
        filteredPlugins.unshift(panelsPlugin())
    }

    const customAccordionBlock = blocks.plugins?.content?.find(i =>
        i.name.endsWith('content/accordion')
    )

    if (customAccordionBlock) {
        const accordionIndex = defaultBlocks.findIndex(i => i.name === 'peracto/content/accordion')
        defaultBlocks.splice(accordionIndex, 1, customAccordionBlock)
    }

    const customCarouselBlock = blocks.plugins?.content?.find(i =>
        i.name.endsWith('content/carousel')
    )

    if (customCarouselBlock) {
        const carouselIndex = defaultBlocks.findIndex(i => i.name === 'peracto/content/carousel')
        defaultBlocks.splice(carouselIndex, 1, customCarouselBlock)
    }

    const customMenuItemsCarouselBlock = blocks.plugins?.content?.find(i =>
        i.name.endsWith('content/menuItemsCarousel')
    )

    if (customMenuItemsCarouselBlock) {
        const menuItemsCarouselIndex = defaultBlocks.findIndex(
            i => i.name === 'peracto/content/menuItemsCarousel'
        )
        defaultBlocks.splice(menuItemsCarouselIndex, 1, customMenuItemsCarouselBlock)
    }

    const customProductsCarousel = blocks.plugins?.content?.find(i =>
        i.name.endsWith('content/productsCarousel')
    )

    if (customProductsCarousel) {
        filteredPlugins.unshift(customProductsCarousel)
    } else if (searchApiKey) {
        filteredPlugins.unshift(productsCarouselPlugin())
    }

    const customProducts = blocks.plugins?.content?.find(i => i.name.endsWith('content/products'))

    if (customProducts) {
        filteredPlugins.unshift(customProducts)
    } else if (searchApiKey) {
        filteredPlugins.unshift(productsPlugin())
    }

    const customButton = blocks.plugins?.content?.find(i => i.name.endsWith('content/buttons'))

    if (customButton) {
        filteredPlugins.push(customButton)
    } else {
        filteredPlugins.push(buttonsPlugin())
    }

    const customMenuItems = blocks.plugins?.content?.find(i => i.name.endsWith('content/menuItems'))

    if (customMenuItems) {
        filteredPlugins.unshift(customMenuItems)
    } else {
        filteredPlugins.unshift(menuItemsPlugin())
    }

    const customEmbedBlock = blocks.plugins?.content?.find(i => i.name.endsWith('content/embed'))

    if (customEmbedBlock) {
        filteredPlugins.unshift(customEmbedBlock)
    } else {
        filteredPlugins.unshift(embedPlugin())
    }

    const customImageBlock = blocks.plugins?.content?.find(i => i.name.endsWith('content/image'))

    if (customImageBlock) {
        defaultBlocks.splice(1, 0, customImageBlock)
    } else {
        defaultBlocks.splice(1, 0, imagePlugin())
    }

    blocks?.plugins?.content?.forEach(block => {
        if (!block.name.startsWith('peracto/')) {
            filteredPlugins.push(block)
        }
    })

    const contentPlugins = {
        content: [...defaultBlocks, ...filteredPlugins],
        layout: [Container],
    }

    const { config = {} } = blocks

    return (
        <div>
            <Helmet>
                {stylesheets.map((style, idx) => (
                    <link rel="stylesheet" href={style} key={`style-${idx}`} />
                ))}
            </Helmet>

            <PeractoEditor
                defaultPlugin={Container}
                plugins={contentPlugins}
                config={config}
                theme={theme}
            />
        </div>
    )
}

export default ContentIndex
